/**
 * FloodDashPanel — Nakhon Ratchasima flood-attention score, bridged from the
 * public FloodDash national flood-watch board (flood.nonarkara.org).
 *
 * This is province-wide (Sikhio has no district-level station density of
 * its own in FloodDash's model), so it's presented as context alongside the
 * Lam Takhong dam reading above it, not a replacement for it — the dam
 * panel is Sikhio-specific telemetry; this is "what does the wider region's
 * attention board say."
 */

import type { FloodDashBrief, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { ActionChecklist } from "./ActionChecklist";
import { Arrow } from "./Arrow";

interface Props {
  data: FloodDashBrief | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const BAND_COLOR: Record<string, string> = {
  normal: "var(--good)",
  watch: "var(--gold)",
  elevated: "var(--warn)",
  high: "var(--bad)",
};

export function FloodDashPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <PanelHeader
          title="เฝ้าระวังภัย "
          fallbackTier={fallbackTier}
          ageMinutes={ageMinutes}
          source="flooddash.nonarkara.org"
        />
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "70%" }} />
      </div>
    );
  }

  const risk = data?.risk;
  if (!risk) {
    return (
      <div className="col" style={{ gap: 4 }}>
        <PanelHeader
          title="เฝ้าระวังภัย "
          fallbackTier={fallbackTier}
          ageMinutes={ageMinutes}
          source="flooddash.nonarkara.org"
        />
        <div className="caption mono" style={{ color: "var(--warn)" }} role="status">
          ◆ FEED GAP · FloodDash unavailable — ไม่ยืนยันความเสี่ยงน้ำท่วมจังหวัด
        </div>
      </div>
    );
  }

  const col = BAND_COLOR[risk.band] ?? "var(--data)";
  const nearestShelter = data?.shelters[0] ?? null;

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="เฝ้าระวังภัย "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="flooddash.nonarkara.org"
        actions={
          <a href={data?.sourceUrl} target="_blank" rel="noreferrer" className="caption mono">
            เปิด <Arrow direction="up-right" />
          </a>
        }
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: "var(--size-h3)", color: col, fontWeight: 700 }}>
          {risk.score}/100
        </span>
        <span className="eyebrow" style={{ color: col }}>
          {risk.headTh}
        </span>
      </div>
      <div
        style={{ height: 5, background: "var(--line)", position: "relative", marginTop: 4 }}
        role="meter"
        aria-valuenow={risk.score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="FloodDash attention score"
      >
        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(100, Math.max(0, risk.score))}%`, background: col }} />
      </div>
      <div className="caption" style={{ marginTop: 4 }}>{risk.actionTh}</div>
      {risk.reasons.length > 0 && (
        <ul className="caption" style={{ margin: "4px 0 0", paddingLeft: 16, color: "var(--text-2)" }}>
          {risk.reasons.map((r) => (
            <li key={r.th}>{r.th}</li>
          ))}
        </ul>
      )}
      {(risk.freeboardMinM != null || risk.hoursToBankMin != null) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 6 }}>
          {risk.freeboardMinM != null && (
            <>
              <span className="caption">เหลือถึงล้นตลิ่ง</span>
              <span className="mono caption" style={{ textAlign: "right" }}>{risk.freeboardMinM.toFixed(2)} ม.</span>
            </>
          )}
          {risk.hoursToBankMin != null && (
            <>
              <span className="caption">คาดถึงใน</span>
              <span className="mono caption" style={{ textAlign: "right" }}>{risk.hoursToBankMin.toFixed(0)} ชม.</span>
            </>
          )}
        </div>
      )}
      {nearestShelter && (
        <div className="caption" style={{ marginTop: 6, color: "var(--text-3)" }}>
          จุดพักพิงใกล้สุดในจังหวัด: {nearestShelter.nameTh} ({nearestShelter.distanceKm.toFixed(0)} กม.
          {nearestShelter.capacity != null ? ` · จุ ${nearestShelter.capacity} คน` : ""})
          <span className="mono">
            {" · "}
            {[
              nearestShelter.hasPower ? "ไฟฟ้า" : null,
              nearestShelter.hasWater ? "น้ำ" : null,
              nearestShelter.hasToilet ? "ส้วม" : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {/* Distance is the operationally decisive number here: the DDPM
              registry has no shelter inside Sikhio, so a high band with a
              40+ km shelter must not read like the council has a local hall. */}
          {nearestShelter.distanceKm > 25 && (
            <div style={{ color: "var(--red)", marginTop: 2 }}>
              ⚠ ไม่มีศูนย์พักพิงในอำเภอสีคิ้ว — จุดที่ใกล้สุดอยู่ต่างอำเภอ{" "}
              {nearestShelter.distanceKm.toFixed(0)} กม. ต้องเตรียมขนส่งประชาชนล่วงหน้า
            </div>
          )}
        </div>
      )}
      <ActionChecklist panelId="flood" band={risk.band} items={risk.checklist} />
      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        {risk.disclaimerTh}
      </div>
    </div>
  );
}
